import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  HelpCircle,
  BookOpen,
  Sparkles,
  Target,
  RefreshCw,
  Users,
  Lightbulb,
  ChevronDown,
  ChevronUp,
  Search,
  PenTool,
  Zap,
  Shield,
  Mail,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui'

interface FAQItem {
  question: string
  answer: string
}

interface GuideSection {
  id: string
  icon: React.ReactNode
  title: string
  description: string
  steps: string[]
}

const guides: GuideSection[] = [
  {
    id: 'getting-started',
    icon: <Sparkles className="w-6 h-6" />,
    title: 'Getting Started',
    description: 'Your first steps with Inkwell Jr.',
    steps: [
      'After signing in, you\'ll select or create a writer profile for your child',
      'Complete the quick onboarding to learn about story structure',
      'Head to the Dashboard to see all available activities',
      'Start with a Story Challenge to get comfortable with guided writing',
    ],
  },
  {
    id: 'dashboard',
    icon: <Target className="w-6 h-6" />,
    title: 'The Dashboard',
    description: 'Your home base for all writing activities',
    steps: [
      'View your writing streak and word count progress',
      'Access Story Challenges, My Stories, and other features',
      'Track achievements and milestones',
      'Switch between different child writers if you have multiple',
    ],
  },
  {
    id: 'stories',
    icon: <BookOpen className="w-6 h-6" />,
    title: 'Writing Stories',
    description: 'Create and manage your stories',
    steps: [
      'Click "My Stories" to see all your saved work',
      'Start a new story with a title and opening idea',
      'Use the writing editor with helpful prompts',
      'Save your progress anytime - it auto-saves too!',
      'Export finished stories as PDF to print or share',
    ],
  },
  {
    id: 'challenges',
    icon: <Zap className="w-6 h-6" />,
    title: 'Story Challenges',
    description: 'Guided writing prompts to spark creativity',
    steps: [
      'Browse available challenges by theme or difficulty',
      'Read the prompt carefully before starting',
      'Write your response in the challenge editor',
      'Submit to see feedback and earn achievements',
      'Revisit past challenges to improve your writing',
    ],
  },
  {
    id: 'characters',
    icon: <Users className="w-6 h-6" />,
    title: 'Character Builder',
    description: 'Create memorable characters for your stories',
    steps: [
      'Go to Characters from the dashboard',
      'Click "New Character" to start the wizard',
      'Answer questions about your character\'s personality',
      'Define what they want and what they fear',
      'Save characters to use in future stories',
    ],
  },
  {
    id: 'revision',
    icon: <RefreshCw className="w-6 h-6" />,
    title: 'Revision Studio',
    description: 'Polish your stories like a real author',
    steps: [
      'Select a completed story to revise',
      'Review suggestions for improving your writing',
      'Focus on one type of revision at a time',
      'Compare your original with the revised version',
      'Learn revision techniques used by professional writers',
    ],
  },
]

const faqs: FAQItem[] = [
  {
    question: 'What age group is Inkwell Jr. designed for?',
    answer: 'Inkwell Jr. is designed for young writers ages 7-12, though creative kids outside this range can enjoy it too! The interface is kid-friendly while teaching real writing concepts.',
  },
  {
    question: 'How do I add another child writer?',
    answer: 'From the dashboard, click your child\'s name in the header to go to writer selection. There you can add additional writer profiles, each with their own stories and progress.',
  },
  {
    question: 'Is my child\'s writing saved automatically?',
    answer: 'Yes! Stories auto-save as your child writes. You can also manually save anytime. All work is stored securely in the cloud.',
  },
  {
    question: 'Can I print or share my child\'s stories?',
    answer: 'Absolutely! Open any completed story and use the Export button to download as a PDF. You can then print it or share it with family and friends.',
  },
  {
    question: 'What is a "writing streak"?',
    answer: 'A writing streak tracks consecutive days of writing activity. It encourages regular practice. Missing a day resets the streak, but all progress and stories remain saved.',
  },
  {
    question: 'How do Story Challenges work?',
    answer: 'Story Challenges are guided prompts that help kids practice specific writing skills. Each challenge has a theme and learning goal. Kids write their response and receive encouraging feedback.',
  },
  {
    question: 'What makes a good story according to Inkwell Jr.?',
    answer: 'We teach the fundamentals: a character who wants something, obstacles in their way, choices that reveal who they are, and a resolution that shows change. These elements create engaging stories at any level.',
  },
  {
    question: 'Is there a limit to how many stories we can create?',
    answer: 'During beta, there are no limits on stories, characters, or writing. We want you to write as much as you like!',
  },
  {
    question: 'How do I contact support?',
    answer: 'Email us at support@inkwelljr.com. We typically respond within 24 hours. For beta users, you can also reach Dave directly.',
  },
  {
    question: 'What happens to our data and stories?',
    answer: 'Your stories and data are stored securely. We never share personal information. You can export your stories anytime, and we\'ll provide full data export if you ever want to leave.',
  },
]

export function Help() {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  const filteredFAQs = faqs.filter(
    (faq) =>
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const toggleFAQ = (index: number) => {
    setExpandedFAQ(expandedFAQ === index ? null : index)
  }

  return (
    <div className="min-h-screen bg-[var(--color-kids-bg)]">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[var(--color-kids-surface)] border-b border-[var(--color-kids-border)] px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link
            to="/dashboard"
            className="p-2 -ml-2 hover:bg-[var(--color-kids-bg)] rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-[var(--color-kids-text-muted)]" />
          </Link>
          <div className="flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[var(--color-kids-primary)]" />
            <h1 className="text-lg font-bold text-[var(--color-kids-text)]">Help & Guide</h1>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--color-kids-primary)]/10 mb-4">
            <Lightbulb className="w-8 h-8 text-[var(--color-kids-primary)]" />
          </div>
          <h2 className="font-display text-2xl font-bold text-[var(--color-kids-text)] mb-2">
            Welcome to Inkwell Jr.
          </h2>
          <p className="text-[var(--color-kids-text-muted)] max-w-lg mx-auto">
            A creative writing adventure for young storytellers. Learn the craft of writing through
            fun challenges, character building, and guided revision.
          </p>
        </div>

        {/* Feature Guides */}
        <section className="mb-12">
          <h3 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-4 flex items-center gap-2">
            <PenTool className="w-5 h-5 text-[var(--color-kids-primary)]" />
            Feature Guides
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {guides.map((guide) => (
              <Card key={guide.id}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="p-2 rounded-xl bg-[var(--color-kids-primary)]/10 text-[var(--color-kids-primary)]">
                      {guide.icon}
                    </div>
                    <div>
                      <h4 className="font-semibold text-[var(--color-kids-text)]">{guide.title}</h4>
                      <p className="text-sm text-[var(--color-kids-text-muted)]">
                        {guide.description}
                      </p>
                    </div>
                  </div>
                  <ul className="space-y-2">
                    {guide.steps.map((step, i) => (
                      <li
                        key={i}
                        className="flex gap-2 text-sm text-[var(--color-kids-text-muted)]"
                      >
                        <span className="text-[var(--color-kids-primary)] font-medium shrink-0">
                          {i + 1}.
                        </span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* FAQs */}
        <section className="mb-12">
          <h3 className="font-display text-xl font-bold text-[var(--color-kids-text)] mb-4 flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-[var(--color-kids-primary)]" />
            Frequently Asked Questions
          </h3>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--color-kids-text-muted)]" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full pl-10 pr-4 py-2 bg-[var(--color-kids-surface)] border border-[var(--color-kids-border)] rounded-xl text-[var(--color-kids-text)] placeholder:text-[var(--color-kids-text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--color-kids-primary)]"
            />
          </div>

          <div className="space-y-2">
            {filteredFAQs.map((faq, index) => (
              <Card key={index}>
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full text-left p-4 flex items-center justify-between gap-4"
                >
                  <span className="font-medium text-[var(--color-kids-text)]">{faq.question}</span>
                  {expandedFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[var(--color-kids-text-muted)] shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-[var(--color-kids-text-muted)] shrink-0" />
                  )}
                </button>
                {expandedFAQ === index && (
                  <div className="px-4 pb-4">
                    <p className="text-[var(--color-kids-text-muted)] text-sm leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </Card>
            ))}
            {filteredFAQs.length === 0 && (
              <p className="text-center py-8 text-[var(--color-kids-text-muted)]">
                No matching questions found. Try a different search term.
              </p>
            )}
          </div>
        </section>

        {/* Contact */}
        <section>
          <Card>
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[var(--color-kids-primary)]/10 mb-4">
                <Mail className="w-6 h-6 text-[var(--color-kids-primary)]" />
              </div>
              <h3 className="font-display text-lg font-bold text-[var(--color-kids-text)] mb-2">
                Still Need Help?
              </h3>
              <p className="text-[var(--color-kids-text-muted)] mb-4">
                We're here to help you and your young writer succeed.
              </p>
              <a
                href="mailto:support@inkwelljr.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--color-kids-primary)] text-white font-semibold rounded-xl hover:bg-[var(--color-kids-primary-hover)] transition-colors"
              >
                <Mail className="w-4 h-4" />
                Contact Support
              </a>
              <p className="text-xs text-[var(--color-kids-text-muted)] mt-3">
                support@inkwelljr.com • We typically respond within 24 hours
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Beta Notice */}
        <div className="mt-8 p-4 bg-[var(--color-kids-primary)]/5 border border-[var(--color-kids-primary)]/20 rounded-xl">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-[var(--color-kids-primary)] mt-0.5" />
            <div>
              <p className="font-medium text-[var(--color-kids-text)]">You're a Beta Tester!</p>
              <p className="text-sm text-[var(--color-kids-text-muted)] mt-1">
                Thank you for helping us build Inkwell Jr. Your feedback shapes the future of
                creative writing for kids. Found a bug or have an idea? Email us directly!
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
